import { Fragment, useEffect, useState, useCallback, useRef } from "react";
import {
  getStatiCassaRiassunto,
  getStatiCassaGiorno,
  exportStatiCassaGiorno,
  listSpese,
  getBancaMovimenti,
  getBancaStats,
  uploadBanca,
  deleteBancaMovimento,
  api,
} from "@/lib/api";
import { openPrintWindow } from "@/lib/printWindow";

const apiGet = (u) => api.get(u);
const apiPost = (u, d) => api.post(u, d);

const TAGLI_EURO = [500, 200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01];

// Ordine colonne modalità nella tabella riassunto: 1=CASSA, poi 2=BANCA-POS (+-1Lav),
// poi 3=BONIFICO (+Banca EC). POS+BON renderizzato come ultima colonna del blocco m=3.
const _MOD_ORDER = { 1: 0, 2: 1, 3: 2 };
const _modSort = (a, b) => (_MOD_ORDER[a?.codice] ?? 99) - (_MOD_ORDER[b?.codice] ?? 99);

function fmtEuro(n) {
  if (n === null || n === undefined) return "0,00";
  return Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function firstDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function toIso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function isoToDmy(iso) { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; }

// Calcola la data POS di default in base al giorno della settimana della riga:
// - lunedì → venerdì precedente (-3 gg)
// - mar/mer/gio/ven/sab/dom → giorno precedente (-1 gg)
function dmyMinusDays(dmy, n) {
  if (!dmy || !/^\d{2}\/\d{2}\/\d{4}$/.test(dmy)) return "";
  const [d, m, y] = dmy.split("/").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - n);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
}
function defaultDataPos(r) {
  const g = (r.giorno_sett || "").toLowerCase();
  // Sempre primo giorno lavorativo PRECEDENTE (lun-ven):
  // - Lun → ven (-3)
  // - Mar-Ven → giorno prec. (-1)
  // - Sab → ven (-1)
  // - Dom → ven (-2)
  let offset = 1;
  if (g.startsWith("lun")) offset = 3;
  else if (g.startsWith("dom")) offset = 2;
  return dmyMinusDays(r.data, offset);
}
function dmyToIsoStr(dmy) {
  if (!dmy || !/^\d{2}\/\d{2}\/\d{4}$/.test(dmy)) return "";
  const [d, m, y] = dmy.split("/");
  return `${y}-${m}-${d}`;
}

export default function StatiCassaPage() {
  const today = new Date();
  const [dataDa, setDataDa] = useState(toIso(firstDayOfMonth(today)));
  const [dataA, setDataA] = useState(toIso(today));
  const [riassunto, setRiassunto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const carica = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await getStatiCassaRiassunto(isoToDmy(dataDa), isoToDmy(dataA));
      setRiassunto(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
      setRiassunto(null);
    } finally { setLoading(false); }
  }, [dataDa, dataA]);

  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="stati-cassa-page">
      <div className="sw-page-title">
        <h1>Stati di Cassa</h1>
        <div className="sw-breadcrumb">
          Home &gt; Stati di Cassa
          {selectedDay && <> &gt; {selectedDay}</>}
        </div>
      </div>

      {!selectedDay ? (
        <RiassuntoView
          dataDa={dataDa} dataA={dataA}
          onChangeDaDa={setDataDa} onChangeDataA={setDataA}
          onCarica={carica} loading={loading} err={err}
          riassunto={riassunto}
          onOpenDay={(dmy) => setSelectedDay(dmy)}
          reload={carica}
        />
      ) : (
        <GiornoView data={selectedDay} onBack={() => setSelectedDay(null)} />
      )}
    </div>
  );
}

function RiassuntoView({ dataDa, dataA, onChangeDaDa, onChangeDataA, onCarica, loading, err, riassunto, onOpenDay, reload }) {
  const [exporting, setExporting] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [editDiff, setEditDiff] = useState(null);
  const [diffVal, setDiffVal] = useState("");
  const [editRiporto, setEditRiporto] = useState(null); // dmy in editing
  const [riportoVal, setRiportoVal] = useState("");
  const [editSaldoCassa, setEditSaldoCassa] = useState(null); // dmy in editing
  const [saldoCassaVal, setSaldoCassaVal] = useState("");
  const [editDataPos, setEditDataPos] = useState(null); // dmy in editing
  const [dataPosVal, setDataPosVal] = useState(""); // yyyy-mm-dd

  const openRiportoEdit = (r) => {
    setEditRiporto(r.data);
    setRiportoVal(r.riporto_saldo !== null && r.riporto_saldo !== undefined ? String(r.riporto_saldo) : "");
  };
  const saveRiporto = async () => {
    try {
      const v = riportoVal.toString().trim();
      const n = v === "" ? null : parseFloat(v.replace(",", "."));
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(editRiporto)}`, { riporto_saldo: n });
      setEditRiporto(null);
      await reload();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const openSaldoCassaEdit = (r) => {
    setEditSaldoCassa(r.data);
    setSaldoCassaVal(r.saldo_cassa !== null && r.saldo_cassa !== undefined ? String(r.saldo_cassa) : "");
  };
  const saveSaldoCassa = async () => {
    try {
      const v = saldoCassaVal.toString().trim();
      const n = v === "" ? null : parseFloat(v.replace(",", "."));
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(editSaldoCassa)}`, { saldo_cassa: n });
      setEditSaldoCassa(null);
      await reload();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const openDiffEdit = (r) => {
    setEditDiff(r.data);
    setDiffVal(r.differenza !== null && r.differenza !== undefined ? String(r.differenza) : "");
  };

  // Editing data POS (associazione data movimenti POS → riga giorno)
  const openDataPosEdit = (r) => {
    setEditDataPos(r.data);
    // Pre-compila SEMPRE col primo giorno lavorativo precedente (default).
    // L'utente può comunque modificare il valore prima di confermare.
    setDataPosVal(dmyToIsoStr(defaultDataPos(r)));
  };
  const saveDataPos = async () => {
    try {
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(editDataPos)}`, { data_pos: dataPosVal || null });
      setEditDataPos(null);
      await reload();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const saveDiff = async () => {
    try {
      const v = diffVal.toString().trim();
      const num = v === "" ? null : parseFloat(v.replace(",", "."));
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(editDiff)}`, { differenza: num });
      setEditDiff(null);
      await reload();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const doExport = async (dmy, mod, label, dataPos, dataA) => {
    setExporting(`${dmy}_${mod}`);
    try {
      const opts = {};
      if (dataPos) opts.data_pos = dataPos;
      if (dataA) opts.data_a = dataA;
      const resp = await exportStatiCassaGiorno(dmy, mod, opts);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const fnamePeriod = dataA
        ? `${dmy.replace(/\//g, "-")}_${dataA.replace(/\//g, "-")}`
        : dmy.replace(/\//g, "-");
      a.download = `cassa_${label}_${fnamePeriod}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Errore export: " + (e.response?.data?.detail || e.message)); }
    finally { setExporting(null); }
  };

  const openPivot = (dmy, mod, label, dataPos, dataA) => {
    setModalType({ type: "pivot", dmy, mod, label, dataPos, dataA });
  };

  // 🆕 Apre il pivot in modalità RANGE usando i filtri Dal/Al correnti
  const openPivotRange = (mod, label) => {
    if (!dataDa || !dataA) { alert("Imposta prima Data da e Data a"); return; }
    const dmy_da = isoToDmy(dataDa);
    const dmy_a = isoToDmy(dataA);
    openPivot(dmy_da, mod, label, null, dmy_a);
  };

  return (
    <>
      <div style={{ margin: "8px 20px", display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", background: "white", border: "1px solid #ccc", padding: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Data da</div>
          <input type="date" data-testid="sc-data-da" value={dataDa} onChange={e => onChangeDaDa(e.target.value)}
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Data a</div>
          <input type="date" data-testid="sc-data-a" value={dataA} onChange={e => onChangeDataA(e.target.value)}
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <button data-testid="btn-carica" onClick={onCarica} disabled={loading}
          style={{ padding: "6px 18px", background: "#0066cc", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          {loading ? "Caricamento..." : "Carica"}
        </button>
        <button data-testid="btn-open-spese-list" onClick={() => setModalType({ type: "spese_list" })}
          style={{ padding: "6px 14px", background: "#ef6c00", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          title="Ricerca tra tutte le spese registrate">
          🔍 Cerca spese
        </button>

        {/* 🆕 Bottoni RANGE: stessa estrazione dei bottoni di riga ma sull'intero periodo Dal-Al */}
        <div style={{ display: "flex", gap: 4, alignItems: "center", borderLeft: "2px solid #ccc", paddingLeft: 10, marginLeft: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#666", marginRight: 4 }}>ESTRAI PERIODO:</span>
          <button data-testid="btn-range-cassa" onClick={() => openPivotRange(1, "CASSA")} disabled={loading}
            style={{ padding: "5px 10px", background: "#1b5e20", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
            title="Estrai movimenti CASSA del periodo Dal-Al">CASSA</button>
          <button data-testid="btn-range-bonifico" onClick={() => openPivotRange("BONIFICO", "BONIFICO")} disabled={loading}
            style={{ padding: "5px 10px", background: "#6a1b9a", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
            title="Estrai movimenti BONIFICO del periodo Dal-Al">BONIFICO</button>
          <button data-testid="btn-range-pos" onClick={() => openPivotRange(2, "POS")} disabled={loading}
            style={{ padding: "5px 10px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
            title="Estrai movimenti POS del periodo Dal-Al">POS</button>
          <button data-testid="btn-range-posbon" onClick={() => openPivotRange("POS_BON", "POS+BON")} disabled={loading}
            style={{ padding: "5px 10px", background: "#00838f", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
            title="Estrai movimenti POS+BON (union) del periodo Dal-Al">POS+BON</button>
          <button data-testid="btn-range-totale" onClick={() => openPivotRange(undefined, "TOTALE")} disabled={loading}
            style={{ padding: "5px 10px", background: "#ef6c00", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
            title="Estrai TUTTI i movimenti del periodo Dal-Al">TOTALE</button>
        </div>

        {riassunto && (
          <div style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700 }}>
            TOTALE PERIODO: <span style={{ color: "#1b5e20" }}>€ {fmtEuro(riassunto.grand_total)}</span>
            <span style={{ fontWeight: 400, color: "#888", fontSize: 11, marginLeft: 10 }}>({riassunto.grand_count} mov) · Saldo finale: € {fmtEuro(riassunto.saldo_finale)}</span>
          </div>
        )}
      </div>

      {err && <div style={{ margin: "0 20px 8px 20px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#cc0000", fontSize: 12 }}>{err}</div>}

      {riassunto && (
        <div style={{ margin: "0 8px 20px 8px", background: "white", border: "1px solid #ccc" }}>
          <div style={{ maxHeight: "72vh", overflow: "auto" }}>
            <table className="sw-table sw-table-compact" style={{ fontSize: 9.5, tableLayout: "auto" }}>
              <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 2 }}>
                <tr>
                  <th style={{ width: 64, padding: "2px 3px" }}>Data</th>
                  <th style={{ width: 32, padding: "2px 3px" }}>Gi.</th>
                  <th style={{ width: 64, padding: "2px 3px", textAlign: "right", background: "#fff3e0" }}>Riporto</th>
                  <th style={{ width: 60, padding: "2px 3px", textAlign: "right", background: "#fce4ec" }}>Diff.</th>
                  {[...riassunto.modalita].sort(_modSort).map(m => {
                    if (m.codice === 8) return null; // Contributo a Cassa renderizzato dopo Cassa
                    const isDivider = m.codice === 1; // dopo blocco CASSA arriverà il separatore
                    return (
                    <Fragment key={`h-${m.codice}`}>
                      <th style={{
                        textAlign: "right", minWidth: 60, padding: "2px 3px",
                        background: m.codice === 1 ? "#e8f5e9" : undefined,
                      }}
                        title={m.codice === 2 ? "Va al giorno lavorativo successivo" : undefined}>
                        {m.codice === 2 ? "POS" : m.descrizione}
                      </th>
                      {m.codice === 2 && (
                        <th data-testid="th-pos-quad"
                          style={{ width: 44, padding: "2px 3px", textAlign: "center", background: "#ede7f6", color: "#4527a0" }}
                          title="Quadratura POS: totalizza gli incassi POS come con una calcolatrice">
                          q$
                        </th>
                      )}
                      {m.codice === 3 && (
                        <th
                          data-testid="th-banca-ec"
                          style={{ textAlign: "right", minWidth: 70, padding: "2px 3px", background: "#e1bee7", color: "#4a148c" }}
                          title="Totale bonifici giornalieri da estratto conto banca (importi positivi della tab BANCA)"
                        >
                          Banca EC
                        </th>
                      )}
                      {m.codice === 3 && (
                        <th
                          data-testid="th-pos-bon"
                          style={{ textAlign: "right", minWidth: 70, padding: "2px 3px", background: "#b2ebf2", color: "#004d40" }}
                          title="Somma colonne BancaPos (POS del -1Lav) + Bonifico"
                        >
                          POS+BON
                        </th>
                      )}
                      {m.codice === 3 && (
                        <th style={{ width: 56, padding: "2px 3px", textAlign: "center", background: "#bbdefb", color: "#0d47a1", borderLeft: "3px solid #0d47a1" }}
                          title="Data POS = primo giorno lavorativo precedente (-1 Lav). Click sul bottone per visualizzare/modificare.">
                          -1Lav
                        </th>
                      )}
                      {isDivider && (
                        <>
                          <th style={{ textAlign: "right", minWidth: 60, padding: "2px 3px", background: "#e1f5fe" }}>Versam.</th>
                          {(() => {
                            const m8 = riassunto.modalita.find(x => x.codice === 8);
                            return m8 ? (
                              <th style={{ textAlign: "right", minWidth: 70, padding: "2px 3px", background: "#e8f5e9" }}>{m8.descrizione}</th>
                            ) : null;
                          })()}
                          <th style={{ textAlign: "right", minWidth: 60, padding: "2px 3px", background: "#fff3e0" }}>Spese</th>
                          <th style={{ textAlign: "right", minWidth: 78, padding: "2px 3px", background: "#c8e6c9", color: "#1b5e20" }}>SALDO</th>
                          <th style={{ width: 50, padding: "2px 3px", textAlign: "center", background: "#f3e5f5", color: "#4a148c", borderRight: "3px solid #333" }}>Qd.</th>
                        </>
                      )}
                    </Fragment>
                  );})}
                  <th style={{ textAlign: "right", background: "#ffd54f", color: "#000", minWidth: 78, padding: "2px 3px" }}>TOTALE €</th>
                  <th style={{ width: 28, padding: "2px 3px", textAlign: "center", background: "#ffd54f" }}>N</th>
                  <th style={{ width: 110, padding: "2px 3px", textAlign: "center", background: "#e1bee7" }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {riassunto.rows.map(r => {
                  const clickable = r.row_count > 0;
                  return (
                    <tr key={r.data} data-testid={`sc-row-${r.data}`}
                        style={{ background: r.is_weekend ? "#f5f5f5" : undefined, color: r.is_weekend ? "#888" : undefined }}>
                      <td style={{ fontFamily: "Consolas", fontWeight: 600, cursor: clickable ? "pointer" : "default" }}
                          onClick={() => clickable && onOpenDay(r.data)}>
                        {r.data}
                      </td>
                      <td style={{ textTransform: "capitalize", cursor: clickable ? "pointer" : "default" }}
                          onClick={() => clickable && onOpenDay(r.data)}>
                        {(r.giorno_sett || "").slice(0, 3)}
                      </td>
                      <td className="num-col" data-testid={`sc-riporto-${r.data}`}
                          onClick={e => { e.stopPropagation(); if (editRiporto !== r.data) openRiportoEdit(r); }}
                          style={{ background: editRiporto === r.data ? "#fff9c4" : (r.riporto_saldo !== null && r.riporto_saldo !== undefined ? "#ffe0b2" : "#f5f5f5"),
                                   color: r.riporto_saldo !== null && r.riporto_saldo !== undefined ? "#e65100" : "#888",
                                   fontWeight: 700, cursor: "pointer" }}
                          title="Riporto saldo del giorno">
                        {editRiporto === r.data ? (
                          <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <input type="number" step="0.01" autoFocus value={riportoVal}
                              onChange={e => setRiportoVal(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") saveRiporto(); if (e.key === "Escape") setEditRiporto(null); }}
                              data-testid={`sc-riporto-input-${r.data}`}
                              style={{ width: "100%", minWidth: 60, padding: "2px 4px", fontSize: 10, border: "1px solid #ef6c00", textAlign: "right", fontFamily: "Consolas" }} />
                            <button onClick={saveRiporto} style={{ background: "#2e7d32", color: "white", border: "none", padding: "1px 4px", fontSize: 10, cursor: "pointer" }} title="Salva">✓</button>
                            <button onClick={() => setEditRiporto(null)} style={{ background: "#999", color: "white", border: "none", padding: "1px 4px", fontSize: 10, cursor: "pointer" }} title="Annulla">✕</button>
                          </div>
                        ) : (
                          r.riporto_saldo === null || r.riporto_saldo === undefined ? <span style={{ color: "#ef6c00", fontSize: 9 }}>+ rip</span> : `€ ${fmtEuro(r.riporto_saldo)}`
                        )}
                      </td>
                      <td className="num-col" data-testid={`sc-differenza-${r.data}`} onClick={e => { e.stopPropagation(); if (editDiff !== r.data) openDiffEdit(r); }}
                          style={{ background: editDiff === r.data ? "#fff9c4" : (r.differenza === null ? "#f5f5f5" : (Math.abs(r.differenza) < 0.01 ? "#e8f5e9" : "#ffebee")),
                                   color: r.differenza === null ? "#888" : (Math.abs(r.differenza) < 0.01 ? "#1b5e20" : "#c62828"),
                                   fontWeight: 700, cursor: "pointer" }}
                          title="Click per inserire o modificare">
                        {editDiff === r.data ? (
                          <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <input type="number" step="0.01" autoFocus value={diffVal}
                              onChange={e => setDiffVal(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") saveDiff(); if (e.key === "Escape") setEditDiff(null); }}
                              data-testid={`sc-differenza-input-${r.data}`}
                              style={{ width: "100%", minWidth: 60, padding: "2px 4px", fontSize: 10, border: "1px solid #1565c0", textAlign: "right", fontFamily: "Consolas" }} />
                            <button onClick={saveDiff} style={{ background: "#2e7d32", color: "white", border: "none", padding: "1px 4px", fontSize: 10, cursor: "pointer" }} title="Salva">✓</button>
                            <button onClick={() => setEditDiff(null)} style={{ background: "#999", color: "white", border: "none", padding: "1px 4px", fontSize: 10, cursor: "pointer" }} title="Annulla">✕</button>
                          </div>
                        ) : (
                          r.differenza === null || r.differenza === undefined ? <span style={{ color: "#0288d1", fontSize: 9 }}>+ diff</span> : `€ ${fmtEuro(r.differenza)}`
                        )}
                      </td>
                      {[...riassunto.modalita].sort(_modSort).map(m => {
                        if (m.codice === 8) return null;
                        const c = r.cells?.[String(m.codice)] || r.cells?.[m.codice];
                        const v = c?.totale || 0;
                        const isDivider = m.codice === 1;
                        return (
                          <Fragment key={`c-${m.codice}`}>
                            <td className="num-col"
                              title={m.codice === 2 ? "Va al giorno lavorativo successivo" : undefined}
                              style={{ background: v !== 0 ? (m.codice === 1 ? "#c8e6c9" : "#fffde7") : undefined, color: v < 0 ? "#cc0000" : undefined }}>
                              {v === 0 ? <span style={{ color: "#ccc" }}>—</span> : fmtEuro(v)}
                            </td>
                            {m.codice === 2 && (() => {
                              const posVal = v;
                              const hasPq = r.pos_quadratura_totale !== null && r.pos_quadratura_totale !== undefined;
                              const quadra = hasPq && Math.abs((r.pos_quadratura_totale || 0) - posVal) < 0.01;
                              const bg = !hasPq ? "white" : (quadra ? "#4527a0" : "#c62828");
                              const col = !hasPq ? "#4527a0" : "white";
                              const border = !hasPq ? "1px solid #4527a0" : (quadra ? "1px solid #4527a0" : "1px solid #c62828");
                              const title = !hasPq
                                ? "Totalizza gli incassi POS come con una calcolatrice. Clicca per aprire."
                                : (quadra
                                    ? `Quadratura POS OK: € ${fmtEuro(r.pos_quadratura_totale)} = POS € ${fmtEuro(posVal)}`
                                    : `Quadratura POS NON quadra: € ${fmtEuro(r.pos_quadratura_totale)} vs POS € ${fmtEuro(posVal)} (Δ € ${fmtEuro((r.pos_quadratura_totale || 0) - posVal)})`);
                              return (
                                <td onClick={e => e.stopPropagation()} style={{ textAlign: "center", padding: 2, background: "#ede7f6" }}>
                                  <button data-testid={`btn-pos-quadratura-${r.data}`}
                                    onClick={() => setModalType({ type: "pos_quadratura", dmy: r.data, posTarget: posVal })}
                                    style={{ padding: "2px 6px", background: bg, color: col, border, fontSize: 9, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                                    title={title}>
                                    {`q$${hasPq ? (quadra ? " ✓" : " ✗") : ""}`}
                                  </button>
                                </td>
                              );
                            })()}
                            {m.codice === 3 && (() => {
                              const vEc = r.banca_bonifici_totale || 0;
                              const diff = Math.round((v - vEc) * 100) / 100;
                              const tooltip = vEc > 0
                                ? `Banca EC: € ${fmtEuro(vEc)} · Bonifici studenti: € ${fmtEuro(v)} · Δ € ${fmtEuro(diff)}`
                                : "Nessun bonifico in estratto conto banca per questo giorno";
                              return (
                                <td
                                  data-testid={`sc-banca-ec-${r.data}`}
                                  className="num-col"
                                  title={tooltip}
                                  style={{
                                    background: vEc !== 0 ? (Math.abs(diff) < 0.01 ? "#e1bee7" : "#fbc02d") : undefined,
                                    color: vEc < 0 ? "#cc0000" : (Math.abs(diff) < 0.01 ? "#4a148c" : "#4e342e"),
                                    fontWeight: vEc !== 0 ? 700 : 400,
                                  }}
                                >
                                  {vEc === 0 ? <span style={{ color: "#ccc" }}>—</span> : fmtEuro(vEc)}
                                </td>
                              );
                            })()}
                            {m.codice === 3 && (() => {
                              const vPosDp = r.pos_data_pos_totale || 0;
                              const vBon = v;
                              const sum = Math.round((vPosDp + vBon) * 100) / 100;
                              const dpEff = r.data_pos_effective;
                              return (
                                <td data-testid={`sc-pos-bon-${r.data}`}
                                  className="num-col"
                                  style={{ background: sum !== 0 ? "#e0f7fa" : undefined,
                                           color: sum < 0 ? "#cc0000" : (sum > 0 ? "#004d40" : "#888"),
                                           fontWeight: sum !== 0 ? 800 : 400 }}
                                  title={`POS della data ${dpEff || "—"}: € ${fmtEuro(vPosDp)} + Bonifico del ${r.data}: € ${fmtEuro(vBon)} = € ${fmtEuro(sum)}`}>
                                  {sum === 0 ? <span style={{ color: "#ccc" }}>—</span> : fmtEuro(sum)}
                                </td>
                              );
                            })()}
                            {m.codice === 3 && (() => {
                              const dpAuto = defaultDataPos(r);
                              const hasSaved = !!r.data_pos;
                              const dpEff = r.data_pos_effective || r.data_pos || dpAuto;
                              const isEditing = editDataPos === r.data;
                              if (!dpEff) {
                                return <td style={{ textAlign: "center", padding: 2, borderLeft: "3px solid #0d47a1" }}>—</td>;
                              }
                              return (
                                <td data-testid={`sc-datapos-${r.data}`}
                                    style={{ textAlign: "center", padding: 2, position: "relative", borderLeft: "3px solid #0d47a1" }}>
                                  <button onClick={e => { e.stopPropagation(); if (isEditing) setEditDataPos(null); else openDataPosEdit(r); }}
                                    data-testid={`sc-datapos-edit-btn-${r.data}`}
                                    title={hasSaved
                                      ? `Data POS: ${r.data_pos} (manuale) — clicca per modificare`
                                      : `Data POS: ${dpEff} (default = giorno lavorativo precedente) — clicca per modificare`}
                                    style={{
                                      background: "#1565c0",
                                      color: "white", border: "none",
                                      padding: "3px 8px", fontSize: 10, fontWeight: 700,
                                      cursor: "pointer", borderRadius: 2,
                                      display: "inline-flex", alignItems: "center", gap: 3,
                                    }}>
                                    📅 -1Lav
                                  </button>
                                  {isEditing && (
                                    <div onClick={e => e.stopPropagation()}
                                      style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
                                               zIndex: 10, background: "white", border: "1px solid #1565c0", borderRadius: 3,
                                               padding: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                                               minWidth: 220, marginTop: 2 }}>
                                      <div style={{ fontSize: 10, fontWeight: 700, color: "#0d47a1", marginBottom: 4 }}>
                                        Data POS {hasSaved ? "" : <span style={{ color: "#90a4ae", fontWeight: 500 }}>(default: {dpAuto})</span>}
                                      </div>
                                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                        <input type="date" autoFocus value={dataPosVal}
                                          onChange={e => setDataPosVal(e.target.value)}
                                          onKeyDown={e => { if (e.key === "Enter") saveDataPos(); if (e.key === "Escape") setEditDataPos(null); }}
                                          data-testid={`sc-datapos-input-${r.data}`}
                                          style={{ flex: 1, padding: "3px 4px", fontSize: 11, border: "1px solid #1565c0", fontFamily: "Consolas" }} />
                                        <button onClick={saveDataPos}
                                          style={{ background: "#2e7d32", color: "white", border: "none", padding: "3px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                                          title="Salva">✓</button>
                                        <button onClick={() => setEditDataPos(null)}
                                          style={{ background: "#999", color: "white", border: "none", padding: "3px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                                          title="Annulla">✕</button>
                                      </div>
                                    </div>
                                  )}
                                </td>
                              );
                            })()}
                            {isDivider && (
                              <>
                                <td className="num-col" style={{ background: r.versamenti ? "#b3e5fc" : undefined, cursor: "pointer", fontWeight: r.versamenti ? 700 : 400 }}
                                    data-testid={`sc-versamenti-${r.data}`}
                                    onClick={() => setModalType({ type: "versamenti", dmy: r.data })}
                                    title="Click per modificare versamenti">
                                  {r.versamenti ? fmtEuro(r.versamenti) : <span style={{ color: "#0288d1", fontSize: 9 }}>+ vers</span>}
                                </td>
                                {(() => {
                                  const c8 = r.cells?.["8"] || r.cells?.[8];
                                  const v8 = c8?.totale || 0;
                                  return (
                                    <td className="num-col" style={{ background: v8 !== 0 ? "#c8e6c9" : undefined, color: v8 < 0 ? "#cc0000" : undefined }}>
                                      {v8 === 0 ? <span style={{ color: "#ccc" }}>—</span> : fmtEuro(v8)}
                                    </td>
                                  );
                                })()}
                                <td className="num-col" style={{ background: r.spese_totale ? "#ffe0b2" : undefined, cursor: "pointer", fontWeight: r.spese_totale ? 700 : 400 }}
                                    data-testid={`sc-spese-${r.data}`}
                                    onClick={() => setModalType({ type: "spese", dmy: r.data })}
                                    title="Click per gestire spese">
                                  {r.spese_totale ? fmtEuro(r.spese_totale) : <span style={{ color: "#ef6c00", fontSize: 9 }}>+ spesa</span>}
                                </td>
                                {(() => {
                                  const riporto = r.riporto_saldo || 0;
                                  const cassaImp = (r.cells?.["1"] || r.cells?.[1] || {}).totale || 0;
                                  const contrCassa = (r.cells?.["8"] || r.cells?.[8] || {}).totale || 0;
                                  const diff = r.differenza || 0;
                                  const saldoCassa = riporto + cassaImp + diff + contrCassa - (r.versamenti || 0) - (r.spese_totale || 0);
                                  return (
                                    <Fragment>
                                    <td className="num-col" data-testid={`sc-totcassa-${r.data}`}
                                        style={{ background: saldoCassa !== 0 ? "#a5d6a7" : "#f5f5f5", fontWeight: 700,
                                                 color: saldoCassa < 0 ? "#cc0000" : (saldoCassa > 0 ? "#1b5e20" : "#888") }}
                                        title="Riporto + Cassa + Differenza + Contributo Cassa − Versamenti − Spese">
                                      € {fmtEuro(saldoCassa)}
                                    </td>
                                    <td onClick={e => e.stopPropagation()} style={{ textAlign: "center", borderRight: "3px solid #333", background: "#f3e5f5" }}>
                                      {(() => {
                                        const hasQuad = r.quadratura_totale !== null && r.quadratura_totale !== undefined;
                                        const quadra = hasQuad && Math.abs((r.quadratura_totale || 0) - saldoCassa) < 0.01;
                                        const bg = !hasQuad ? "white" : (quadra ? "#6a1b9a" : "#c62828");
                                        const col = !hasQuad ? "#6a1b9a" : "white";
                                        const border = !hasQuad ? "1px solid #6a1b9a" : (quadra ? "1px solid #6a1b9a" : "1px solid #c62828");
                                        const title = !hasQuad
                                          ? "Quadratura non effettuata"
                                          : (quadra
                                              ? `Quadratura OK: € ${fmtEuro(r.quadratura_totale)} = saldo cassa`
                                              : `Quadratura NON quadra: € ${fmtEuro(r.quadratura_totale)} vs saldo € ${fmtEuro(saldoCassa)} (diff € ${fmtEuro((r.quadratura_totale || 0) - saldoCassa)})`);
                                        return (
                                          <button data-testid={`btn-quadratura-${r.data}`} onClick={() => setModalType({ type: "quadratura", dmy: r.data })}
                                            style={{ padding: "2px 6px", background: bg, color: col, border, fontSize: 9, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                                            title={title}>
                                            {`q$${hasQuad ? (quadra ? " ✓" : " ✗") : ""}`}
                                          </button>
                                        );
                                      })()}
                                    </td>
                                    </Fragment>
                                  );
                                })()}
                              </>
                            )}
                          </Fragment>
                        );
                      })}
                      <td className="num-col" style={{ background: r.row_total !== 0 ? "#fff59d" : "#f5f5f5", fontWeight: 700, color: r.row_total > 0 ? "#1b5e20" : (r.row_total < 0 ? "#cc0000" : "#888") }}>
                        € {fmtEuro(r.row_total)}
                      </td>
                      <td className="center-col">{r.row_count || ""}</td>
                      <td onClick={e => e.stopPropagation()} style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {r.row_count > 0 && (
                          <button data-testid={`btn-export-cassa-${r.data}`} onClick={() => openPivot(r.data, 1, "CASSA")}
                            style={{ padding: "2px 6px", background: "#1b5e20", color: "white", border: "none", fontSize: 9, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}
                            title="Visualizza dettaglio CASSA del giorno">CASSA</button>
                        )}
                        {r.row_count > 0 && (
                          <button data-testid={`btn-export-bonifico-${r.data}`} onClick={() => openPivot(r.data, "BONIFICO", "BONIFICO")}
                            style={{ padding: "2px 6px", background: "#6a1b9a", color: "white", border: "none", fontSize: 9, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}
                            title="Visualizza dettaglio BONIFICI del giorno">BONIFICO</button>
                        )}
                        {r.row_count > 0 && r.data_pos_effective && (
                          <button data-testid={`btn-export-pos-${r.data}`} onClick={() => openPivot(r.data, 2, "POS")}
                            style={{ padding: "2px 6px", background: "#1565c0", color: "white", border: "none", fontSize: 9, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}
                            title={`Visualizza movimenti POS della data ${r.data}`}>POS</button>
                        )}
                        {r.row_count > 0 && r.data_pos_effective && (
                          <button data-testid={`btn-export-posbon-${r.data}`} onClick={() => openPivot(r.data, "POS_BON", "POS+BON", r.data_pos_effective)}
                            style={{ padding: "2px 6px", background: "#00838f", color: "white", border: "none", fontSize: 9, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}
                            title={`Movimenti POS (${r.data_pos_effective}) + BONIFICO (${r.data}) che sommano al totale POS+BON`}>POS+BON</button>
                        )}
                        {r.row_count > 0 && (
                          <button data-testid={`btn-export-totale-${r.data}`} onClick={() => openPivot(r.data, undefined, "TOTALE")}
                            style={{ padding: "2px 6px", background: "#ef6c00", color: "white", border: "none", fontSize: 9, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}
                            title="Visualizza TUTTI i movimenti del giorno">TOTALE</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: "#e0e0e0" }}>
                  <td colSpan={2}>TOTALI</td>
                  <td></td>{/* riporto */}
                  <td></td>{/* differenza */}
                  {[...riassunto.modalita].sort(_modSort).map(m => {
                    if (m.codice === 8) return null;
                    const t = riassunto.col_totals?.[String(m.codice)] || { totale: 0, num: 0 };
                    const isDivider = m.codice === 1;
                    return (
                      <Fragment key={`t-${m.codice}`}>
                        <td className="num-col" style={{ background: "#fff59d" }}>
                          € {fmtEuro(t.totale)}
                        </td>
                        {m.codice === 3 && (
                          <td data-testid="sc-banca-ec-totale" className="num-col"
                            style={{ background: "#e1bee7", color: "#4a148c", fontWeight: 800 }}
                            title="Totale bonifici da estratto conto banca nel periodo">
                            € {fmtEuro(riassunto.totale_banca_bonifici || 0)}
                          </td>
                        )}
                        {m.codice === 3 && (() => {
                          const totPosDp = riassunto.totale_pos_data_pos || 0;
                          const totBon = (riassunto.col_totals?.["3"] || {}).totale || 0;
                          const totSum = Math.round((totPosDp + totBon) * 100) / 100;
                          return (
                            <td data-testid="sc-pos-bon-totale" className="num-col"
                              style={{ background: "#80deea", color: "#004d40", fontWeight: 800 }}
                              title={`Tot POS (data_pos): € ${fmtEuro(totPosDp)} + Tot Bonifico: € ${fmtEuro(totBon)} = € ${fmtEuro(totSum)}`}>
                              € {fmtEuro(totSum)}
                            </td>
                          );
                        })()}
                        {m.codice === 3 && (
                          <td style={{ background: "#bbdefb", borderLeft: "3px solid #0d47a1" }}></td>
                        )}
                        {isDivider && (
                          <>
                            <td className="num-col" style={{ background: "#b3e5fc" }}>€ {fmtEuro(riassunto.totale_versamenti)}</td>
                            {(() => {
                              const t8 = riassunto.col_totals?.["8"] || { totale: 0 };
                              return (<td className="num-col" style={{ background: "#fff59d" }}>€ {fmtEuro(t8.totale)}</td>);
                            })()}
                            <td className="num-col" style={{ background: "#ffe0b2" }}>€ {fmtEuro(riassunto.totale_spese)}</td>
                            {(() => {
                              const tot = (riassunto.rows || []).reduce((s, r) => {
                                const riporto = r.riporto_saldo || 0;
                                const cassaImp = (r.cells?.["1"] || r.cells?.[1] || {}).totale || 0;
                                const contrCassa = (r.cells?.["8"] || r.cells?.[8] || {}).totale || 0;
                                return s + riporto + cassaImp + (r.differenza || 0) + contrCassa - (r.versamenti || 0) - (r.spese_totale || 0);
                              }, 0);
                              return (<td className="num-col" style={{ background: "#66bb6a", color: "white", fontSize: 12 }} title="Somma saldi cassa del periodo">€ {fmtEuro(tot)}</td>);
                            })()}
                            <td style={{ background: "#f3e5f5", borderRight: "3px solid #333" }}></td>
                          </>
                        )}
                      </Fragment>
                    );
                  })}
                  <td className="num-col" style={{ background: "#ffb300", fontSize: 12 }}>€ {fmtEuro(riassunto.grand_total)}</td>
                  <td className="center-col" style={{ background: "#ffb300" }}>{riassunto.grand_count}</td>
                  <td style={{ background: "#e1bee7" }}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {modalType?.type === "spese" && (
        <SpeseModal dmy={modalType.dmy} onClose={() => setModalType(null)} onSaved={async () => { setModalType(null); await reload(); }} />
      )}
      {modalType?.type === "versamenti" && (
        <VersamentiModal dmy={modalType.dmy} onClose={() => setModalType(null)} onSaved={async () => { setModalType(null); await reload(); }} />
      )}
      {modalType?.type === "quadratura" && (
        <QuadraturaModal dmy={modalType.dmy} onClose={() => setModalType(null)} onSaved={async () => { setModalType(null); await reload(); }} />
      )}
      {modalType?.type === "pos_quadratura" && (
        <PosQuadraturaModal dmy={modalType.dmy} posTarget={modalType.posTarget} onClose={() => setModalType(null)} onSaved={async () => { setModalType(null); await reload(); }} />
      )}
      {modalType?.type === "pivot" && (
        <PivotModalitaModal dmy={modalType.dmy} mod={modalType.mod} label={modalType.label} dataPos={modalType.dataPos} dataA={modalType.dataA}
          onClose={() => setModalType(null)}
          onExport={() => doExport(modalType.dmy, modalType.mod, modalType.label, modalType.dataPos, modalType.dataA)}
          exporting={exporting === `${modalType.dmy}_${modalType.mod}`} />
      )}
      {modalType?.type === "spese_list" && (
        <SpeseListModal initialFrom={dataDa} initialTo={dataA} onClose={() => setModalType(null)} onNavigate={(dmy) => { setModalType({ type: "spese", dmy }); }} />
      )}
    </>
  );
}

function ModalShell({ title, onClose, children, color = "#6a1b9a", wide = false, footer = null }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: "100%", maxWidth: wide ? 700 : 500, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
        <div style={{ background: color, color: "white", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer", fontWeight: 700 }}>✕</button>
        </div>
        <div style={{ padding: 16, overflow: "auto", flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid #e0e0e0", background: "#fafafa", flexShrink: 0 }}>{footer}</div>
        )}
      </div>
    </div>
  );
}

function SpeseModal({ dmy, onClose, onSaved }) {
  const [spese, setSpese] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`).then(r => {
      setSpese(r.data.spese || []);
    });
  }, [dmy]);

  const add = () => setSpese([...spese, { descrizione: "", importo: 0 }]);
  const remove = (i) => setSpese(spese.filter((_, idx) => idx !== i));
  const update = (i, f, v) => setSpese(spese.map((s, idx) => idx === i ? { ...s, [f]: v } : s));
  const totale = spese.reduce((s, x) => s + (parseFloat(x.importo) || 0), 0);

  const salva = async () => {
    setSaving(true);
    try {
      const clean = spese.map(s => ({ descrizione: s.descrizione, importo: parseFloat(s.importo) || 0 })).filter(s => s.descrizione || s.importo);
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`, { spese: clean });
      await onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Spese del giorno ${dmy}`} onClose={onClose} color="#ef6c00" wide>
      <table className="sw-table" style={{ fontSize: 12, width: "100%" }}>
        <thead>
          <tr><th>Descrizione</th><th style={{ width: 130 }}>Importo €</th><th style={{ width: 40 }}></th></tr>
        </thead>
        <tbody>
          {spese.map((s, i) => (
            <tr key={i}>
              <td><input type="text" data-testid={`spesa-desc-${i}`} value={s.descrizione} onChange={e => update(i, "descrizione", e.target.value)} style={{ width: "100%", padding: "4px 6px", border: "1px solid #ccc" }} /></td>
              <td><input type="number" step="0.01" data-testid={`spesa-imp-${i}`} value={s.importo} onChange={e => update(i, "importo", e.target.value)} style={{ width: "100%", padding: "4px 6px", border: "1px solid #ccc", textAlign: "right", fontFamily: "Consolas" }} /></td>
              <td style={{ textAlign: "center" }}>
                <button onClick={() => remove(i)} style={{ background: "transparent", border: "none", color: "#c62828", cursor: "pointer", fontSize: 16 }} title="Rimuovi">🗑</button>
              </td>
            </tr>
          ))}
          {spese.length === 0 && <tr><td colSpan={3} style={{ textAlign: "center", color: "#888", padding: 20 }}>Nessuna spesa. Clicca "+ Aggiungi spesa".</td></tr>}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700, background: "#fff3e0" }}>
            <td style={{ textAlign: "right" }}>TOTALE SPESE</td>
            <td className="num-col">€ {fmtEuro(totale)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button data-testid="btn-add-spesa" onClick={add} style={{ padding: "6px 14px", background: "#ef6c00", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Aggiungi spesa</button>
        <button data-testid="btn-save-spese" onClick={salva} disabled={saving} style={{ marginLeft: "auto", padding: "6px 18px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{saving ? "Salvataggio..." : "Salva e chiudi"}</button>
      </div>
    </ModalShell>
  );
}

function VersamentiModal({ dmy, onClose, onSaved }) {
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`).then(r => {
      setVal(String(r.data.versamenti || 0));
    });
  }, [dmy]);

  const salva = async () => {
    setSaving(true);
    try {
      const n = parseFloat(val.toString().replace(",", ".")) || 0;
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`, { versamenti: n });
      await onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Versamenti del giorno ${dmy}`} onClose={onClose} color="#0288d1">
      <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
        Importo versato in banca (uscita cassa). Questo valore viene sottratto dal saldo atteso.
      </div>
      <input type="number" step="0.01" data-testid="versamenti-input" value={val} onChange={e => setVal(e.target.value)} autoFocus
        style={{ width: "100%", padding: "8px 12px", border: "1px solid #ccc", fontSize: 16, fontFamily: "Consolas", textAlign: "right", boxSizing: "border-box" }} />
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <button data-testid="btn-save-versamenti" onClick={salva} disabled={saving}
          style={{ marginLeft: "auto", padding: "6px 18px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{saving ? "..." : "Salva"}</button>
      </div>
    </ModalShell>
  );
}

function QuadraturaModal({ dmy, onClose, onSaved }) {
  const [tagli, setTagli] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    apiGet(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`).then(r => {
      setTagli((r.data.quadratura?.tagli) || {});
      setLoaded(true);
    });
  }, [dmy]);

  const update = (taglio, qty) => {
    const key = String(taglio);
    const n = parseInt(qty, 10) || 0;
    setTagli({ ...tagli, [key]: n });
  };

  const totale = TAGLI_EURO.reduce((s, t) => s + t * (parseInt(tagli[String(t)], 10) || 0), 0);

  const salva = async () => {
    setSaving(true);
    try {
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`, { quadratura: { tagli } });
      await onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const reset = () => setTagli({});

  const footer = loaded ? (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <button data-testid="btn-reset-quadratura" onClick={reset} style={{ padding: "6px 14px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Azzera</button>
      <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>Totale: <b style={{ color: "#6a1b9a" }}>€ {fmtEuro(totale)}</b></div>
      <button data-testid="btn-close-quadratura" onClick={onClose} style={{ padding: "6px 14px", background: "white", color: "#555", border: "1px solid #bbb", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
      <button data-testid="btn-save-quadratura" onClick={salva} disabled={saving}
        style={{ padding: "6px 18px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer" }}>{saving ? "Salvataggio..." : "💾 Salva e chiudi"}</button>
    </div>
  ) : null;

  return (
    <ModalShell title={`Quadratura cassa - ${dmy}`} onClose={onClose} color="#6a1b9a" wide footer={footer}>
      {!loaded ? <div className="sw-loading">Caricamento...</div> : (
        <>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
            Inserisci le quantità di ogni taglio per totalizzare quanto hai in cassa fisicamente.
          </div>
          <table className="sw-table" style={{ fontSize: 13, width: "100%" }}>
            <thead>
              <tr style={{ background: "#f3e5f5" }}>
                <th style={{ width: 100 }}>Taglio</th>
                <th style={{ width: 100 }}>Quantità</th>
                <th style={{ textAlign: "right" }}>Subtotale</th>
              </tr>
            </thead>
            <tbody>
              {TAGLI_EURO.map(t => {
                const n = parseInt(tagli[String(t)], 10) || 0;
                const sub = t * n;
                const isBanconota = t >= 5;
                return (
                  <tr key={t}>
                    <td style={{ fontWeight: 700, background: isBanconota ? "#ede7f6" : "#f5f5f5", fontFamily: "Consolas" }}>
                      € {fmtEuro(t)} {isBanconota && <span style={{ fontSize: 10, color: "#888", fontWeight: 400 }}>banconota</span>}
                    </td>
                    <td>
                      <input
                        type="number" min="0" step="1"
                        data-testid={`taglio-${t}`}
                        value={n || ""}
                        onChange={e => update(t, e.target.value)}
                        style={{ width: 90, padding: "4px 8px", border: "1px solid #ccc", fontSize: 14, textAlign: "right", fontFamily: "Consolas" }}
                      />
                    </td>
                    <td className="num-col" style={{ fontFamily: "Consolas", background: sub > 0 ? "#f3e5f5" : undefined, fontWeight: sub > 0 ? 700 : 400 }}>
                      {sub ? `€ ${fmtEuro(sub)}` : <span style={{ color: "#ccc" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: "#6a1b9a", color: "white" }}>
                <td colSpan={2} style={{ textAlign: "right", padding: "10px 12px" }}>TOTALE CASSA FISICA</td>
                <td className="num-col" style={{ fontSize: 16, padding: "10px 12px" }}>€ {fmtEuro(totale)}</td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </ModalShell>
  );
}

function PosQuadraturaModal({ dmy, posTarget, onClose, onSaved }) {
  const [voci, setVoci] = useState([]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    apiGet(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`).then(r => {
      setVoci((r.data.pos_quadratura?.voci) || []);
      setLoaded(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    });
  }, [dmy]);

  const totale = voci.reduce((s, x) => s + (parseFloat(x) || 0), 0);

  const addVoce = () => {
    const n = parseFloat(String(input).replace(",", "."));
    if (!isNaN(n) && n !== 0) {
      setVoci(prev => [...prev, Math.round(n * 100) / 100]);
    }
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const removeVoce = (idx) => setVoci(prev => prev.filter((_, i) => i !== idx));

  const salva = async () => {
    setSaving(true);
    try {
      await apiPost(`/stati-cassa/daily-meta?data=${encodeURIComponent(dmy)}`, { pos_quadratura: { voci } });
      await onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const reset = () => { setVoci([]); setInput(""); setTimeout(() => inputRef.current?.focus(), 10); };

  const hasTarget = posTarget !== null && posTarget !== undefined;
  const quadra = hasTarget && Math.abs(totale - posTarget) < 0.01;

  const footer = loaded ? (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <button data-testid="btn-reset-pos-quadratura" onClick={reset} style={{ padding: "6px 14px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Azzera</button>
      <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>Totale POS: <b style={{ color: "#4527a0" }}>€ {fmtEuro(totale)}</b></div>
      <button data-testid="btn-close-pos-quadratura" onClick={onClose} style={{ padding: "6px 14px", background: "white", color: "#555", border: "1px solid #bbb", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
      <button data-testid="btn-save-pos-quadratura" onClick={salva} disabled={saving}
        style={{ padding: "6px 18px", background: "#4527a0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer" }}>{saving ? "Salvataggio..." : "💾 Salva e chiudi"}</button>
    </div>
  ) : null;

  return (
    <ModalShell title={`Quadratura POS - ${dmy}`} onClose={onClose} color="#4527a0" footer={footer}>
      {!loaded ? <div className="sw-loading">Caricamento...</div> : (
        <>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
            Digita ogni incasso POS e premi <b>Invio</b>: gli importi vengono sommati come con una calcolatrice.
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 12 }}>
            <input
              ref={inputRef}
              type="number" step="0.01" inputMode="decimal"
              data-testid="pos-quad-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addVoce(); } if (e.key === "Escape") onClose(); }}
              placeholder="Incasso POS e Invio..."
              style={{ flex: 1, padding: "10px 12px", border: "2px solid #4527a0", fontSize: 18, textAlign: "right", fontFamily: "Consolas", borderRadius: 4 }}
            />
            <button data-testid="pos-quad-add" onClick={addVoce}
              style={{ padding: "10px 16px", background: "#4527a0", color: "white", border: "none", fontSize: 16, fontWeight: 700, cursor: "pointer", borderRadius: 4 }}>+ Invio</button>
          </div>
          <table className="sw-table" style={{ fontSize: 13, width: "100%" }}>
            <thead>
              <tr style={{ background: "#ede7f6" }}>
                <th style={{ width: 50, textAlign: "center" }}>#</th>
                <th style={{ textAlign: "right" }}>Incasso POS</th>
                <th style={{ width: 60, textAlign: "center" }}>—</th>
              </tr>
            </thead>
            <tbody>
              {voci.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: 20, color: "#aaa", fontStyle: "italic" }}>Nessun incasso inserito.</td></tr>
              ) : voci.map((x, i) => (
                <tr key={i} data-testid={`pos-quad-voce-${i}`}>
                  <td style={{ textAlign: "center", color: "#888", fontFamily: "Consolas" }}>{i + 1}</td>
                  <td className="num-col" style={{ fontFamily: "Consolas", fontWeight: 700 }}>€ {fmtEuro(x)}</td>
                  <td style={{ textAlign: "center" }}>
                    <button data-testid={`pos-quad-del-${i}`} onClick={() => removeVoce(i)}
                      style={{ background: "white", color: "#c62828", border: "1px solid #c62828", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "1px 7px" }} title="Rimuovi">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: "#4527a0", color: "white" }}>
                <td style={{ textAlign: "right", padding: "10px 12px" }}>{voci.length}</td>
                <td className="num-col" style={{ fontSize: 16, padding: "10px 12px" }} data-testid="pos-quad-totale">€ {fmtEuro(totale)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          {hasTarget && (
            <div data-testid="pos-quad-confronto"
              style={{ marginTop: 12, padding: "8px 12px", borderRadius: 4, fontSize: 13, fontWeight: 700,
                       background: quadra ? "#e8f5e9" : "#ffebee", color: quadra ? "#1b5e20" : "#c62828" }}>
              POS giornata: € {fmtEuro(posTarget)} &nbsp;·&nbsp; {quadra ? "✓ Quadra" : `✗ Δ € ${fmtEuro(totale - posTarget)}`}
            </div>
          )}
        </>
      )}
    </ModalShell>
  );
}

function PivotModalitaModal({ dmy, mod, label, dataPos, dataA, onClose, onExport, exporting }) {
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(null);
      try {
        const opts = {};
        if (dataPos && !dataA) opts.data_pos = dataPos;
        if (dataA) opts.data_a = dataA;
        const r = await getStatiCassaGiorno(dmy, undefined, mod, opts);
        setDati(r.data);
      } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
      finally { setLoading(false); }
    })();
  }, [dmy, mod, dataPos, dataA]);

  const color = label === "CASSA" ? "#1b5e20" : label === "POS" ? "#1565c0" : label === "BONIFICO" ? "#6a1b9a" : label === "POS+BON" ? "#00838f" : "#ef6c00";
  // 🆕 Titolo dinamico: "del {dmy}" per singolo giorno, "dal {dmy} al {dataA}" per range
  const periodLabel = dataA ? `dal ${dmy} al ${dataA}` : `del ${dmy}`;

  const handleStampa = () => {
    const area = document.getElementById("pivot-print-area");
    if (!area) return;
    const css = `
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; margin: 12px; color: #000; }
      h1 { font-size: 14px; margin: 0 0 4px 0; color: ${color}; }
      .meta { font-size: 10px; color: #555; margin-bottom: 10px; }
      table { border-collapse: collapse; width: 100%; font-size: 9px; }
      th, td { border: 1px solid #999; padding: 3px 5px; vertical-align: middle; }
      th { background: #1f3864; color: #fff; font-weight: 700; text-align: center; }
      td.num-col, th.num-col { text-align: right; font-family: Consolas, monospace; }
      tfoot td { font-weight: 700; background: #fff2cc; }
      @page { size: landscape; margin: 10mm; }
      @media print {
        .no-print { display: none !important; }
      }
    `;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${label} ${periodLabel}</title><style>${css}</style></head>
      <body>
        <h1>${label} ${periodLabel}</h1>
        <div class="meta">Dettaglio movimenti · plessi × gruppi causali · Stampato il ${new Date().toLocaleString("it-IT")}</div>
        ${area.innerHTML}
      </body></html>`;
    const w = openPrintWindow(html, { autoPrint: true });
    if (!w) { alert("Il browser ha bloccato la finestra di stampa. Consenti i popup per procedere."); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: "100vw", height: "100vh", maxWidth: "100vw", maxHeight: "100vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
        <div style={{ background: color, color: "white", padding: "5px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
              {label} {periodLabel}
              {dataPos && !dataA && <span style={{ fontSize: 10, opacity: 0.85, marginLeft: 8 }}>(POS data: {dataPos})</span>}
            </div>
            <div style={{ fontSize: 10, opacity: 0.9 }}>Dettaglio movimenti · plessi × gruppi causali{dataA ? " · PERIODO" : ""}</div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button data-testid="btn-pivot-print" onClick={handleStampa} disabled={loading || !dati}
              style={{ padding: "3px 10px", background: "white", color, border: "none", fontSize: 11, fontWeight: 700, cursor: loading || !dati ? "not-allowed" : "pointer", borderRadius: 2, opacity: loading || !dati ? 0.5 : 1 }}>
              🖨 Stampa
            </button>
            <button data-testid="btn-pivot-export" onClick={onExport} disabled={exporting}
              style={{ padding: "3px 10px", background: "white", color, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
              {exporting ? "..." : "📥 Excel"}
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer", fontWeight: 700, padding: "0 4px" }}>✕</button>
          </div>
        </div>
        <div style={{ padding: 4, overflow: "auto", flex: 1, minHeight: 0 }}>
          {loading && <div className="sw-loading">Caricamento...</div>}
          {err && <div style={{ padding: 14, background: "#fff0f0", border: "1px solid #fcc", color: "#cc0000" }}>{err}</div>}
          {dati && <div id="pivot-print-area"><PivotModalitaTable dati={dati} /></div>}
        </div>
      </div>
    </div>
  );
}

function PivotModalitaTable({ dati }) {
  const gruppiSettori = dati.gruppi_settori || [];
  const gruppiCau = dati.gruppi_causali || [];
  const plessoColors = { "INFANZIA": "#fff2cc", "PRIMARIA": "#e2efda", "SECONDARIA": "#ddebf7" };

  // Filtro alunno (client-side)
  const [filtroAlunno, setFiltroAlunno] = useState("");
  const fq = filtroAlunno.trim().toLowerCase();
  const movimentiAll = dati.movimenti || [];
  const movimentiFiltrati = fq
    ? movimentiAll.filter(m =>
        (m.alu_nome || "").toLowerCase().includes(fq) ||
        String(m.alu_cod || "").toLowerCase().includes(fq) ||
        (m.descrizione || "").toLowerCase().includes(fq)
      )
    : movimentiAll;

  // Pre-calcolo totali per (plesso, gruppo causale)
  const totals = {};
  for (const m of movimentiFiltrati) {
    const pk = m.plesso_cod === null || m.plesso_cod === undefined ? "_altri" : String(m.plesso_cod);
    const gc = m.gruppo_cau_codice != null ? String(m.gruppo_cau_codice) : "_nc";
    const k = `${pk}|${gc}`;
    totals[k] = (totals[k] || 0) + (m.importo || 0);
  }

  // Raggruppa i movimenti per cau_codice preservando l'ordine di arrivo dal backend
  // (che ha già ordinato per cau → plesso → alunno). Per ogni gruppo calcolo subtotali per cella.
  const cauGroups = [];
  const cauGroupIdx = {};
  for (const m of movimentiFiltrati) {
    const cc = m.cau_codice ?? "_nc";
    let idx = cauGroupIdx[cc];
    if (idx === undefined) {
      idx = cauGroups.length;
      cauGroupIdx[cc] = idx;
      cauGroups.push({
        cau_codice: m.cau_codice,
        cau_descrizione: m.cau_descrizione || (m.cau_codice == null ? "(senza causale)" : `Causale ${m.cau_codice}`),
        movimenti: [],
        cell_totals: {},   // key `${plesso}|${gruppoCau}` → €
        tot_riga: 0,
      });
    }
    const g = cauGroups[idx];
    g.movimenti.push(m);
    const pk = m.plesso_cod === null || m.plesso_cod === undefined ? "_altri" : String(m.plesso_cod);
    const gc = m.gruppo_cau_codice != null ? String(m.gruppo_cau_codice) : "_nc";
    const k = `${pk}|${gc}`;
    g.cell_totals[k] = (g.cell_totals[k] || 0) + (m.importo || 0);
    g.tot_riga += (m.importo || 0);
  }

  const totalColumns = 3 + gruppiSettori.length * gruppiCau.length + 1;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, padding: "0 4px", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, fontWeight: 700 }}>
          Movimenti: {fq ? `${movimentiFiltrati.length} di ${movimentiAll.length}` : movimentiAll.length} · Totale: <span style={{ color: "#1b5e20" }}>€ {fmtEuro(fq ? movimentiFiltrati.reduce((s,m) => s + (m.importo || 0), 0) : dati.totale_giorno)}</span>
        </div>
        <input type="text" value={filtroAlunno} onChange={e => setFiltroAlunno(e.target.value)}
          data-testid="staticassa-mov-search-alunno"
          placeholder="🔍 Filtra alunno (cognome, nome, codice, descrizione)..."
          style={{ padding: "4px 8px", border: "1px solid #1976d2", borderRadius: 3, fontSize: 11, minWidth: 280, fontWeight: 600 }} />
      </div>
      <table className="sw-table sw-table-compact" style={{ fontSize: 9, width: "100%", tableLayout: "auto" }}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ width: 38, background: "#1f3864", color: "white", padding: "2px 3px" }}>#</th>
            <th rowSpan={2} style={{ background: "#1f3864", color: "white", padding: "2px 4px", minWidth: 130 }}>Alunno</th>
            <th rowSpan={2} style={{ background: "#1f3864", color: "white", padding: "2px 4px", minWidth: 160 }}>Descrizione</th>
            {gruppiSettori.map(g => {
              const up = (g.descrizione || "").toUpperCase();
              return (
                <th key={g.codice} colSpan={gruppiCau.length} style={{ textAlign: "center", background: plessoColors[up] || "#ccc", color: "#000", fontWeight: 700, padding: "2px 3px" }}>
                  {up}
                </th>
              );
            })}
            <th rowSpan={2} style={{ textAlign: "right", minWidth: 78, background: "#ffc000", color: "#000", padding: "2px 4px" }}>TOTALE</th>
          </tr>
          <tr>
            {gruppiSettori.map(g => {
              const up = (g.descrizione || "").toUpperCase();
              return gruppiCau.map(gc => (
                <th key={`${g.codice}-${gc.codice}`} style={{ background: plessoColors[up] || "#ccc", color: "#000", fontSize: 8, textAlign: "right", minWidth: 56, padding: "2px 3px" }}>
                  {gc.descrizione}
                </th>
              ));
            })}
          </tr>
        </thead>
        <tbody>
          {cauGroups.length === 0 && (
            <tr>
              <td colSpan={totalColumns} style={{ textAlign: "center", padding: 30, color: "#888", fontStyle: "italic" }}>Nessun movimento con questa modalità per il giorno selezionato.</td>
            </tr>
          )}
          {cauGroups.map((grp, gi) => {
            return (
              <Fragment key={`cau-${grp.cau_codice ?? "_nc"}-${gi}`}>
                {/* Header di gruppo causale */}
                <tr style={{ background: "#1565c0", color: "white", fontWeight: 700 }}>
                  <td colSpan={totalColumns} style={{ padding: "3px 8px", fontSize: 10 }}>
                    <span style={{ background: "#ffc000", color: "#000", padding: "1px 7px", borderRadius: 8, marginRight: 8, fontFamily: "Consolas, monospace", fontWeight: 700 }}>
                      #{grp.cau_codice ?? "—"}
                    </span>
                    {grp.cau_descrizione} <span style={{ opacity: 0.85, fontWeight: 400 }}>· {grp.movimenti.length} mov.</span>
                  </td>
                </tr>
                {/* Righe singoli movimenti del gruppo */}
                {grp.movimenti.map(m => {
                  const pkMatch = m.plesso_cod === null || m.plesso_cod === undefined ? "_altri" : String(m.plesso_cod);
                  return (
                    <tr key={m.num}>
                      <td style={{ fontFamily: "Consolas", color: "#888", padding: "1px 3px" }}>{m.num}</td>
                      <td style={{ fontWeight: 600, padding: "1px 4px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={`${m.alu_nome || ""}${m.alu_cod ? ` · #${m.alu_cod}` : ""}${m.settore ? ` · ${m.settore}` : ""}`}>
                        {m.alu_nome || "—"}
                        {m.alu_cod && <span style={{ fontSize: 8, color: "#888", fontWeight: 400, marginLeft: 4 }}>#{m.alu_cod}{m.settore ? ` · ${m.settore}` : ""}</span>}
                      </td>
                      <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "1px 4px" }}
                          title={`${m.descrizione || ""} ${m.nota || ""}`.trim()}>
                        {m.descrizione || "—"}
                      </td>
                      {gruppiSettori.map(g => {
                        const pk = String(g.codice);
                        return gruppiCau.map(gc => {
                          const match = pk === pkMatch && m.gruppo_cau_codice === gc.codice;
                          return (
                            <td key={`${pk}-${gc.codice}`} className="num-col"
                                style={{ background: match ? "#fff59d" : undefined, fontWeight: match ? 700 : 400, padding: "1px 3px" }}>
                              {match ? fmtEuro(m.importo) : ""}
                            </td>
                          );
                        });
                      })}
                      <td className="num-col" style={{ background: "#fff9c4", fontWeight: 700, color: m.importo < 0 ? "#cc0000" : "#1b5e20", padding: "1px 4px" }}>€ {fmtEuro(m.importo)}</td>
                    </tr>
                  );
                })}
                {/* Subtotale per causale */}
                <tr style={{ background: "#e3f2fd", fontWeight: 700, color: "#0d47a1" }}>
                  <td colSpan={3} style={{ padding: "3px 8px", textAlign: "right", fontSize: 10 }}>
                    SUBTOTALE #{grp.cau_codice ?? "—"} {grp.cau_descrizione}
                    <span style={{ marginLeft: 6, opacity: 0.75, fontWeight: 400 }}>({grp.movimenti.length} mov.)</span>
                  </td>
                  {gruppiSettori.map(g => {
                    const pk = String(g.codice);
                    return gruppiCau.map(gc => {
                      const k = `${pk}|${gc.codice}`;
                      const v = grp.cell_totals[k] || 0;
                      return (
                        <td key={`sub-${pk}-${gc.codice}`} className="num-col"
                            style={{ padding: "2px 3px", background: v !== 0 ? "#bbdefb" : undefined, fontFamily: "Consolas, monospace", color: v !== 0 ? "#0d47a1" : "#b0bec5" }}>
                          {v === 0 ? "—" : fmtEuro(v)}
                        </td>
                      );
                    });
                  })}
                  <td className="num-col" style={{ background: "#90caf9", color: "#0d47a1", fontFamily: "Consolas, monospace", fontWeight: 700, padding: "2px 4px" }}>
                    € {fmtEuro(grp.tot_riga)}
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700, background: "#fff2cc", position: "sticky", bottom: 0 }}>
            <td colSpan={3} style={{ padding: "2px 4px" }}>TOTALI per plesso × gruppo causale</td>
            {gruppiSettori.map(g => {
              const pk = String(g.codice);
              return gruppiCau.map(gc => {
                const tot = totals[`${pk}|${gc.codice}`] || 0;
                return (
                  <td key={`${pk}-${gc.codice}`} className="num-col" style={{ background: tot !== 0 ? "#fff9c4" : "#f5f5f5", padding: "2px 3px" }}>
                    {tot === 0 ? <span style={{ color: "#ccc" }}>—</span> : fmtEuro(tot)}
                  </td>
                );
              });
            })}
            <td className="num-col" style={{ background: "#ffc000", fontSize: 11, padding: "2px 4px" }}>€ {fmtEuro(dati.totale_giorno)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}


function GiornoView({ data, onBack }) {
  const [giorno, setGiorno] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await getStatiCassaGiorno(data);
        setGiorno(r.data);
      } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
      finally { setLoading(false); }
    })();
  }, [data]);

  if (loading) return <div className="sw-loading" style={{ marginTop: 40 }}>Caricamento giorno...</div>;
  if (err) return <div style={{ margin: 20, padding: 14, background: "#fff0f0", border: "1px solid #fcc", color: "#cc0000" }}>{err}</div>;
  if (!giorno) return null;

  const gruppiSettori = giorno.gruppi_settori || [];
  const hasSenzaPlesso = !!giorno.pivot_plessi._altri;
  const plessiKeys = [...gruppiSettori.map(g => String(g.codice)), ...(hasSenzaPlesso ? ["_altri"] : [])];
  const gruppiCau = giorno.gruppi_causali || [];

  return (
    <>
      <div style={{ margin: "8px 20px", padding: 12, background: "#e8f5e9", border: "1px solid #81c784", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <button data-testid="btn-back-riassunto" onClick={onBack}
          style={{ padding: "5px 14px", background: "white", color: "#1b5e20", border: "1px solid #1b5e20", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          ← Torna al riassunto
        </button>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          Giorno {giorno.data} <span style={{ color: "#666", fontWeight: 400, textTransform: "capitalize" }}>({giorno.giorno_sett})</span>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 14, fontWeight: 700 }}>
          TOTALE GIORNO: <span style={{ color: "#1b5e20" }}>€ {fmtEuro(giorno.totale_giorno)}</span>
          <span style={{ fontWeight: 400, color: "#666", fontSize: 12, marginLeft: 8 }}>({giorno.num_movimenti} mov)</span>
        </div>
      </div>

      <div style={{ margin: "0 20px 12px 20px", background: "white", border: "1px solid #ccc" }}>
        <div style={{ padding: "6px 10px", background: "#f5f5f5", borderBottom: "1px solid #ccc", fontSize: 12, fontWeight: 700 }}>
          Riepilogo plessi × gruppi causali
        </div>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ width: 160 }}>Plesso</th>
              {gruppiCau.map(g => (<th key={g.codice} style={{ textAlign: "right", minWidth: 110 }}>{g.descrizione}</th>))}
              <th style={{ textAlign: "right", background: "#ffd54f", minWidth: 120 }}>TOT. PLESSO</th>
            </tr>
          </thead>
          <tbody>
            {plessiKeys.map(pk => {
              const p = giorno.pivot_plessi[pk];
              if (!p) return null;
              return (
                <tr key={pk}>
                  <td style={{ fontWeight: 700 }}>{p.plesso_descrizione}</td>
                  {gruppiCau.map(g => {
                    const c = p.cells?.[String(g.codice)];
                    const v = c?.totale || 0;
                    return (<td key={g.codice} className="num-col" style={{ background: v !== 0 ? "#fffde7" : undefined }}>{v === 0 ? <span style={{ color: "#ccc" }}>—</span> : fmtEuro(v)}</td>);
                  })}
                  <td className="num-col" style={{ background: p.totale !== 0 ? "#fff59d" : "#f5f5f5", fontWeight: 700, color: p.totale > 0 ? "#1b5e20" : "#888" }}>€ {fmtEuro(p.totale)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ margin: "0 20px 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <div style={{ padding: "6px 10px", background: "#f5f5f5", borderBottom: "1px solid #ccc", fontSize: 12, fontWeight: 700 }}>Movimenti del giorno</div>
        <div style={{ maxHeight: "55vh", overflow: "auto" }}>
          <table className="sw-table sw-table-compact" style={{ fontSize: 10 }}>
            <thead style={{ position: "sticky", top: 0, background: "#e8e8e8" }}>
              <tr>
                <th style={{ width: 55 }}>#</th>
                <th>Alunno</th>
                <th style={{ width: 80 }}>Plesso</th>
                <th>Causale</th>
                <th>Descrizione</th>
                <th style={{ width: 95 }}>Modalità</th>
                <th style={{ textAlign: "right", width: 90, background: "#ffd54f" }}>Importo</th>
              </tr>
            </thead>
            <tbody>
              {giorno.movimenti.map(m => (
                <tr key={m.num}>
                  <td style={{ fontFamily: "Consolas", color: "#888" }}>{m.num}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{m.alu_nome || "—"}</div>
                    {m.alu_cod && <div style={{ fontSize: 9, color: "#888" }}>#{m.alu_cod} {m.settore && `· ${m.settore}`}</div>}
                  </td>
                  <td>
                    <span style={{ background: m.plesso_cod === "_altri" ? "#eee" : "#e3f2fd", color: m.plesso_cod === "_altri" ? "#666" : "#0d47a1", fontSize: 9, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>
                      {m.plesso_descrizione}
                    </span>
                  </td>
                  <td>{m.cau_descrizione || "—"}</td>
                  <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${m.descrizione || ""} ${m.nota || ""}`.trim()}>{m.descrizione || "—"}</td>
                  <td>{m.mod_descrizione}</td>
                  <td className="num-col" style={{ background: "#fff59d", fontWeight: 600, color: m.importo < 0 ? "#cc0000" : "#1b5e20" }}>€ {fmtEuro(m.importo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}


function SpeseListModal({ initialFrom, initialTo, onClose, onNavigate }) {
  const [from, setFrom] = useState(initialFrom || "");
  const [to, setTo] = useState(initialTo || "");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const loadData = async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (from) params.data_da = isoToDmy(from);
      if (to) params.data_a = isoToDmy(to);
      if (search.trim()) params.search = search.trim();
      const r = await listSpese(params);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento spese");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); loadData(); } };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div onClick={e => e.stopPropagation()} data-testid="spese-list-modal"
        style={{ background: "white", width: "100%", maxWidth: 900, maxHeight: "92vh", display: "flex", flexDirection: "column", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ background: "#ef6c00", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>🔍 Ricerca spese</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #ddd", background: "#fff8e1", display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#666", marginBottom: 2 }}>Data da</div>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="sp-from"
              style={{ padding: "4px 8px", border: "1px solid #bbb", fontSize: 12 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#666", marginBottom: 2 }}>Data a</div>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="sp-to"
              style={{ padding: "4px 8px", border: "1px solid #bbb", fontSize: 12 }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#666", marginBottom: 2 }}>Cerca descrizione</div>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={onKey}
              placeholder="Cancelleria, bollo, luce..." data-testid="sp-search"
              style={{ width: "100%", padding: "4px 8px", border: "1px solid #bbb", fontSize: 12, boxSizing: "border-box" }} />
          </div>
          <button onClick={loadData} disabled={loading} data-testid="sp-go"
            style={{ padding: "6px 16px", background: "#ef6c00", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {loading ? "..." : "Cerca"}
          </button>
        </div>
        {err && <div style={{ margin: "10px 16px 0", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }}>{err}</div>}
        <div style={{ flex: 1, overflow: "auto", padding: "0 16px 14px" }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</div>
          ) : data ? (
            <>
              <div style={{ padding: "10px 0", fontSize: 12, color: "#555", display: "flex", justifyContent: "space-between" }}>
                <span>Trovate <b>{data.total}</b> spese</span>
                <span>Totale: <b style={{ color: "#ef6c00" }}>€ {fmtEuro(data.totale_importo)}</b></span>
              </div>
              <table className="sw-table sw-table-compact" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>DATA</th>
                    <th>DESCRIZIONE</th>
                    <th style={{ width: 110, textAlign: "right" }}>IMPORTO</th>
                    <th style={{ width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessuna spesa trovata</td></tr>
                  ) : data.data.map((r, i) => (
                    <tr key={i} data-testid={`sp-row-${i}`}>
                      <td style={{ fontFamily: "Consolas, monospace" }}>{r.data}</td>
                      <td>{r.descrizione || <span style={{ color: "#999" }}>(senza descrizione)</span>}</td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#ef6c00" }}>€ {fmtEuro(r.importo)}</td>
                      <td style={{ textAlign: "center" }}>
                        <button onClick={() => onNavigate(r.data)} title="Apri giorno" data-testid={`sp-open-${i}`}
                          style={{ padding: "2px 8px", background: "#0066cc", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                          Apri
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// BANCA — Estratto conto bancario importato da Excel
// ============================================================================
export function BancaView() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Default: ultimi 30 giorni (verrà allineato a data_max degli stats appena disponibili)
  const _today = new Date();
  const _ago = new Date(_today); _ago.setDate(_today.getDate() - 30);
  const [from, setFrom] = useState(toIso(_ago));
  const [to, setTo] = useState(toIso(_today));
  const [search, setSearch] = useState("");
  const [importoFilter, setImportoFilter] = useState("");
  const [segno, setSegno] = useState("");
  const [showOnlyKo, setShowOnlyKo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tolleranzaGg, setTolleranzaGg] = useState(3);
  const [reqSeq, setReqSeq] = useState(0);
  // 🗑 Feb 2026: cancellazione movimento singolo (solo admin)
  const [deleteTarget, setDeleteTarget] = useState(null); // { _id, data, descrizione, importo }
  const [deleting, setDeleting] = useState(false);

  const askDelete = (row) => setDeleteTarget(row);
  const confirmDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await deleteBancaMovimento(deleteTarget._id);
      setDeleteTarget(null);
      await loadStats();
      await loadData();
    } catch (ex) {
      alert(ex.response?.data?.detail || "Errore cancellazione movimento");
    } finally {
      setDeleting(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await getBancaStats();
      setStats(r.data);
      if (r.data?.data_max) {
        const dm = r.data.data_max;
        const [dd, mm, yy] = dm.split("/");
        const dEnd = new Date(`${yy}-${mm}-${dd}`);
        const dStart = new Date(dEnd); dStart.setDate(dEnd.getDate() - 30);
        setTo(toIso(dEnd));
        setFrom(toIso(dStart));
      }
      return r.data;
    } catch { return null; }
  };

  const loadData = useCallback(async (overrides = {}) => {
    const myReq = Date.now();
    setReqSeq(myReq);
    setLoading(true); setErr(null);
    try {
      const params = { tolleranza_gg: tolleranzaGg };
      const _from = overrides.from !== undefined ? overrides.from : from;
      const _to = overrides.to !== undefined ? overrides.to : to;
      if (_from) params.data_da = isoToDmy(_from);
      if (_to) params.data_a = isoToDmy(_to);
      if (search.trim()) params.search = search.trim();
      if (segno) params.segno = segno;
      const _imp = parseFloat(String(importoFilter).replace(",", ".").trim());
      if (!isNaN(_imp) && _imp !== 0) params.importo = _imp;
      const r = await getBancaMovimenti(params);
      setReqSeq(cur => {
        if (cur !== myReq) return cur;
        setData(r.data);
        return cur;
      });
    } catch (e) {
      setReqSeq(cur => {
        if (cur !== myReq) return cur;
        setErr(e.response?.data?.detail || "Errore caricamento");
        setData(null);
        return cur;
      });
    } finally {
      setReqSeq(cur => {
        if (cur === myReq) setLoading(false);
        return cur;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, search, segno, tolleranzaGg, importoFilter]);

  useEffect(() => {
    (async () => {
      const s = await loadStats();
      if (s && s.total > 0 && s.data_max) {
        const dm = s.data_max; const [dd, mm, yy] = dm.split("/");
        const dEnd = new Date(`${yy}-${mm}-${dd}`);
        const dStart = new Date(dEnd); dStart.setDate(dEnd.getDate() - 30);
        await loadData({ from: toIso(dStart), to: toIso(dEnd) });
      } else if (s && s.total === 0) {
        setData({ days: [], total_count: 0, total_entrate: 0, total_uscite: 0, total_netto: 0, riconc_giorni_ok: 0, riconc_giorni_ko: 0 });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); loadData(); } };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true); setErr(null);
    try {
      // Modalità fissa "append": l'archivio bancario viene sempre arricchito,
      // mai svuotato (la lista banca si aggiorna incrementalmente da Excel).
      // Il backend deduplica automaticamente per (data, importo, descrizione).
      const r = await uploadBanca(file, "append");
      const d = r.data || {};
      alert(
        `Importazione completata\n\n` +
        `• Nuovi movimenti inseriti: ${d.inserted ?? 0}\n` +
        `• Duplicati già presenti (ignorati): ${d.duplicates ?? 0}\n` +
        `• Righe scartate (dati non validi): ${d.skipped ?? 0}`
      );
      await loadStats();
      await loadData();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  };

  // Filtri client-side: solo giorni non quadrati + filtro testuale su descrizione.
  const visibleDays = (() => {
    const baseDays = (data?.days || []).filter(g => {
      if (!showOnlyKo) return true;
      return g.riconc_ok === false;
    });
    const q = (search || "").trim().toLowerCase();
    if (q) {
      return baseDays.map(g => ({
        ...g,
        rows: (g.rows || []).filter(r => (r.descrizione || "").toLowerCase().includes(q)),
      })).filter(g => g.rows.length > 0);
    }
    return baseDays;
  })();

  return (
    <>
      <div style={{ margin: "8px 20px", padding: 12, background: "white", border: "1px solid #ccc", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Data da</div>
          <input type="date" data-testid="bk-from" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Data a</div>
          <input type="date" data-testid="bk-to" value={to} onChange={e => setTo(e.target.value)} style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Cerca descrizione</div>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={onKey} data-testid="bk-search"
            placeholder="Bonifico, descrizione..." style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Importo €</div>
          <input type="text" inputMode="decimal" value={importoFilter} onChange={e => setImportoFilter(e.target.value)} onKeyDown={onKey} data-testid="bk-importo"
            placeholder="es. 620,00" title="Cerca movimenti di questo importo (entrate o uscite)"
            style={{ width: 110, padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }} />
        </div>
        <button onClick={loadData} disabled={loading} data-testid="bk-carica"
          style={{ padding: "6px 18px", background: "#0066cc", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          {loading ? "Caricamento..." : "Filtra"}
        </button>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#555", cursor: "pointer" }}>
          <input type="checkbox" checked={showOnlyKo} onChange={e => setShowOnlyKo(e.target.checked)} data-testid="bk-only-ko" />
          Solo giorni NON quadrati
        </label>
      </div>

      <div style={{ margin: "0 20px 8px 20px", padding: 12, background: "#f0f8ff", border: "1px solid #b3d9ff", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <div style={{ fontWeight: 700, color: "#003366", fontSize: 12 }}>Importa estratto conto Excel</div>
        <label data-testid="bk-upload-label" style={{ padding: "6px 14px", background: uploading ? "#888" : "#1976d2", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: uploading ? "wait" : "pointer", borderRadius: 2 }}>
          {uploading ? "Caricamento..." : "Carica file .xlsx"}
          <input type="file" accept=".xlsx,.xls" onChange={handleUpload} disabled={uploading} data-testid="bk-upload" style={{ display: "none" }} />
        </label>
        <button
          onClick={() => {
            const API = process.env.REACT_APP_BACKEND_URL;
            const params = new URLSearchParams({
              data_da: from ? isoToDmy(from) : "",
              data_a: to ? isoToDmy(to) : "",
              tolleranza_gg: String(tolleranzaGg),
            });
            if (search && search.trim()) params.set("search", search.trim());
            const _imp = parseFloat(String(importoFilter).replace(",", ".").trim());
            if (!isNaN(_imp) && _imp !== 0) params.set("importo", String(_imp));
            window.open(`${API}/api/stati-cassa/banca/pdf?${params.toString()}`, "_blank");
          }}
          data-testid="bk-print-pdf"
          title="Stampa PDF raggruppato per giorno (un giorno per pagina)"
          style={{ padding: "6px 14px", background: "#6a1b9a", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
          📄 Stampa PDF
        </button>
        <button
          onClick={async () => {
            try {
              const API = process.env.REACT_APP_BACKEND_URL;
              // dry-run
              const r1 = await fetch(`${API}/api/stati-cassa/banca/dedupe?dry_run=true`, { method: "POST", credentials: "include" });
              const j1 = await r1.json();
              const groups = j1.duplicate_groups || 0;
              const toDel = j1.records_to_delete || 0;
              if (toDel === 0) { alert("Nessun documento doppio trovato nell'archivio bancario."); return; }
              if (!window.confirm(`Trovati ${groups} gruppi di righe duplicate.\nVerranno eliminate ${toDel} righe (mantenendo la prima copia).\n\nConfermi l'eliminazione definitiva?`)) return;
              const r2 = await fetch(`${API}/api/stati-cassa/banca/dedupe?dry_run=false`, { method: "POST", credentials: "include" });
              const j2 = await r2.json();
              alert(`Rimosse ${j2.deleted || 0} righe duplicate (${j2.duplicate_groups || 0} gruppi).`);
              await loadStats();
              await loadData();
            } catch (ex) {
              alert("Errore durante la pulizia: " + (ex?.message || ex));
            }
          }}
          data-testid="bk-dedupe"
          title="Cerca ed elimina i documenti doppi (stessa data, importo e descrizione)"
          style={{ padding: "6px 14px", background: "#c62828", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
          🧹 Elimina doppi
        </button>
        {stats && (
          <div style={{ marginLeft: "auto", fontSize: 11, color: "#003366" }} data-testid="bk-stats">
            <strong>{stats.total}</strong> righe in archivio
            {stats.data_min && <> · dal <strong>{stats.data_min}</strong> al <strong>{stats.data_max}</strong></>}
          </div>
        )}
      </div>

      {err && <div style={{ margin: "0 20px 8px 20px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#cc0000", fontSize: 12 }}>{err}</div>}

      {data && (
        <div style={{ margin: "0 20px 8px 20px", padding: "10px 14px", background: "#fff8e1", border: "1px solid #ffe082", display: "flex", gap: 22, flexWrap: "wrap", fontSize: 12 }}>
          <div><strong>{data.total_count}</strong> movimenti · <strong>{(data.days || []).length}</strong> giorni</div>
          <div>Entrate: <strong style={{ color: "#1b5e20" }}>€ {fmtEuro(data.total_entrate)}</strong></div>
          <div>Uscite: <strong style={{ color: "#c62828" }}>€ {fmtEuro(data.total_uscite)}</strong></div>
          <div>Netto: <strong style={{ color: data.total_netto >= 0 ? "#1b5e20" : "#c62828" }}>€ {fmtEuro(data.total_netto)}</strong></div>
          <div title="Giorni in cui il totale entrate bancarie coincide con il totale dei movimenti studenti pagati per BONIFICO nello stesso giorno (Δ < € 0,01)">
            Giorni quadrati: <strong style={{ color: "#1b5e20" }}>{data.riconc_giorni_ok || 0}</strong>
            <span style={{ color: "#c62828", marginLeft: 6 }}>NON quadrati: <strong>{data.riconc_giorni_ko || 0}</strong></span>
            {data.riconciliazione_skip_motivo === "range_oltre_366gg" && (
              <span title="Riconciliazione disattivata: il range è oltre 366 giorni."
                style={{ marginLeft: 6, fontSize: 10, color: "#c62828", fontWeight: 700 }}>(disattivata)</span>
            )}
          </div>
        </div>
      )}

      <div data-testid="bk-table-container" style={{ margin: "0 20px 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <div style={{ maxHeight: "75vh", overflow: "auto" }}>
          <table className="sw-table sw-table-compact" style={{ fontSize: 11, width: "100%" }}>
            <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 2 }}>
              <tr>
                <th style={{ width: 95 }}>Data</th>
                <th>Descrizione</th>
                <th style={{ width: 110, textAlign: "right" }}>Importo €</th>
                <th style={{ width: 50, textAlign: "center" }}>🗑</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>
              ) : visibleDays.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                  {stats?.total === 0 ? "Nessun dato presente. Carica un file Excel per iniziare." : "Nessun movimento trovato con i filtri impostati."}
                </td></tr>
              ) : visibleDays.map(g => {
                const okClr = g.riconc_ok === true ? "#1b5e20" : g.riconc_ok === false ? "#b71c1c" : "#666";
                const okBg = g.riconc_ok === true ? "#c8e6c9" : g.riconc_ok === false ? "#ffcdd2" : "#eee";
                return (
                <Fragment key={g.data}>
                  {/* Header di gruppo per data con totali */}
                  <tr data-testid={`bk-day-${g.data}`} style={{ background: "#dbe7f5", fontWeight: 700 }}>
                    <td style={{ fontFamily: "Consolas, monospace", color: "#0d47a1", fontSize: 12 }}>
                      {g.data}
                    </td>
                    <td colSpan={3} style={{ color: "#0d47a1", fontSize: 11 }}>
                      <span style={{ marginRight: 14 }}>{g.rows.length} movimenti</span>
                      <span style={{ color: "#1b5e20", fontWeight: 600 }}>Entrate: € {fmtEuro(g.subtotale_entrate)}</span>
                      <span style={{ marginLeft: 14, color: "#555", fontWeight: 500 }}>Bonifici studenti: € {fmtEuro(g.bonifici_studenti_totale || 0)}</span>
                      <span style={{ marginLeft: 14, fontFamily: "Consolas, monospace", color: g.subtotale >= 0 ? "#1b5e20" : "#c62828", fontWeight: 700 }}>
                        Totale: € {fmtEuro(g.subtotale)}
                      </span>
                      {g.riconc_ok !== null && (
                        <span style={{ marginLeft: 14, background: okBg, color: okClr, padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}
                          data-testid={`bk-day-status-${g.data}`}>
                          {g.riconc_ok ? "✓ Quadrato" : `✗ Δ € ${fmtEuro(g.delta_riconc || 0)}`}
                        </span>
                      )}
                    </td>
                  </tr>
                  {/* Righe del giorno SEMPRE espanse */}
                  {g.rows.map((r, i) => (
                    <tr key={`${g.data}-${i}`} data-testid={`bk-row-${g.data}-${i}`}>
                      <td style={{ fontFamily: "Consolas, monospace", color: "#666" }}>{r.data}</td>
                      <td style={{ whiteSpace: "normal", wordBreak: "break-word", fontSize: 10, lineHeight: 1.35, padding: "4px 6px" }}>
                        {r.descrizione || <span style={{ color: "#aaa" }}>(senza descrizione)</span>}
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 600, color: r.importo >= 0 ? "#1b5e20" : "#c62828" }}>
                        € {fmtEuro(r.importo)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          onClick={() => askDelete(r)}
                          data-testid={`bk-delete-${g.data}-${i}`}
                          title="Cancella movimento (solo admin)"
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#c62828",
                            cursor: "pointer",
                            fontSize: 14,
                            padding: "2px 6px",
                          }}
                        >🗑</button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {deleteTarget && (
        <div
          data-testid="bk-delete-modal"
          onClick={() => !deleting && setDeleteTarget(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white", width: 460, maxWidth: "90vw", padding: 22,
              border: "2px solid #b71c1c", boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 12, color: "#b71c1c", fontSize: 17 }}>
              🗑 Conferma cancellazione movimento
            </h3>
            <div style={{ fontSize: 13, color: "#333", marginBottom: 14 }}>
              Stai per cancellare <b>definitivamente</b> questo movimento bancario. L&apos;operazione non è reversibile.
            </div>
            <div style={{ background: "#fafafa", border: "1px solid #ddd", padding: "10px 12px", fontSize: 12, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                <span style={{ color: "#666", minWidth: 90 }}>Data:</span>
                <b style={{ fontFamily: "Consolas, monospace" }}>{deleteTarget.data || "—"}</b>
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                <span style={{ color: "#666", minWidth: 90 }}>Descrizione:</span>
                <span style={{ wordBreak: "break-word" }}>{deleteTarget.descrizione || "(senza descrizione)"}</span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <span style={{ color: "#666", minWidth: 90 }}>Importo:</span>
                <b style={{
                  fontFamily: "Consolas, monospace",
                  color: (deleteTarget.importo || 0) >= 0 ? "#1b5e20" : "#c62828",
                }}>€ {fmtEuro(deleteTarget.importo)}</b>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                data-testid="bk-delete-cancel"
                style={{
                  padding: "8px 18px", background: "#eee", color: "#333",
                  border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}
              >Annulla</button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="bk-delete-confirm"
                style={{
                  padding: "8px 18px",
                  background: deleting ? "#888" : "#c62828",
                  color: "white", border: "none", fontSize: 12, fontWeight: 700,
                  cursor: deleting ? "wait" : "pointer",
                }}
              >{deleting ? "Cancellazione..." : "🗑 Cancella"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

